/**
 * Design store — persisted user preferences for app appearance.
 *
 * Stores per-user design choices in localStorage so they survive page refreshes.
 * Provides an `applyDesign()` function that writes CSS custom properties to
 * the document root, making the changes take effect immediately across the
 * entire app without a reload.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

// ─── Types ───────────────────────────────────────────────────────────────────

export type AccentPreset = "brass" | "cobalt" | "sage" | "terracotta" | "slate" | "custom";
export type SidebarTheme = "dark" | "medium" | "light";
export type FontChoice = "dm-sans" | "geist" | "inter";
export type Density = "compact" | "normal" | "spacious";
export type Rounding = "sharp" | "soft" | "round";

export interface DesignPreferences {
  accent: AccentPreset;
  customAccentHex: string;   // used when accent === "custom"
  sidebar: SidebarTheme;
  appBackgroundHex: string;
  surfaceBackgroundHex: string;
  cardBackgroundHex: string;
  headerBackgroundHex: string;
  textHex: string;
  mutedTextHex: string;
  faintTextHex: string;
  sidebarBackgroundHex: string;
  sidebarTextHex: string;
  sidebarMutedHex: string;
  sidebarHighlightHex: string;
  font: FontChoice;
  density: Density;
  rounding: Rounding;
}

// ─── Palette data ─────────────────────────────────────────────────────────────

export const ACCENT_PRESETS: Record<AccentPreset, { label: string; base: string; light: string; dim: string }> = {
  brass:      { label: "Messing",    base: "#B8943F", light: "#D4AF60", dim: "rgba(184,148,63,0.12)" },
  cobalt:     { label: "Kobalt",     base: "#2D5BA8", light: "#4E7FCC", dim: "rgba(45,91,168,0.12)" },
  sage:       { label: "Salie",      base: "#4A7C59", light: "#6BAF85", dim: "rgba(74,124,89,0.12)" },
  terracotta: { label: "Terracotta", base: "#A84832", light: "#C96B50", dim: "rgba(168,72,50,0.12)" },
  slate:      { label: "Leisteen",   base: "#4A5568", light: "#718096", dim: "rgba(74,85,104,0.12)" },
  custom:     { label: "Aangepast",  base: "#B8943F", light: "#D4AF60", dim: "rgba(184,148,63,0.12)" },
};

export const SIDEBAR_THEMES: Record<SidebarTheme, {
  label: string;
  bg: string; text: string; muted: string;
  border: string; hover: string; active: string;
}> = {
  dark: {
    label: "Donker",
    bg: "#1A1712", text: "#C8BFB0", muted: "#7A7060",
    border: "rgba(255,255,255,0.06)", hover: "rgba(255,255,255,0.05)", active: "rgba(184,148,63,0.15)",
  },
  medium: {
    label: "Leisgrijs",
    bg: "#2D3748", text: "#CBD5E0", muted: "#718096",
    border: "rgba(255,255,255,0.08)", hover: "rgba(255,255,255,0.06)", active: "rgba(184,148,63,0.18)",
  },
  light: {
    label: "Licht",
    bg: "#F7F3EC", text: "#1C1914", muted: "#6B6355",
    border: "rgba(28,25,20,0.08)", hover: "rgba(28,25,20,0.04)", active: "rgba(184,148,63,0.10)",
  },
};

export const FONT_CHOICES: Record<FontChoice, { label: string; family: string; preview: string }> = {
  "dm-sans": { label: "DM Sans",    family: "'DM Sans', sans-serif",     preview: "Aa Bb 0123" },
  "geist":   { label: "Geist",      family: "'Geist Variable', sans-serif", preview: "Aa Bb 0123" },
  "inter":   { label: "Inter",      family: "'Inter', system-ui, sans-serif", preview: "Aa Bb 0123" },
};

export const DENSITY_OPTIONS: Record<Density, { label: string; spacing: string; cardPadding: string }> = {
  compact:  { label: "Compact",  spacing: "1.25rem", cardPadding: "0.875rem 1rem" },
  normal:   { label: "Normaal",  spacing: "1.75rem", cardPadding: "1.25rem"       },
  spacious: { label: "Ruim",     spacing: "2.5rem",  cardPadding: "1.75rem"       },
};

export const ROUNDING_OPTIONS: Record<Rounding, { label: string; radius: string }> = {
  sharp: { label: "Scherp",   radius: "0.125rem" },
  soft:  { label: "Zacht",    radius: "0.5rem"   },
  round: { label: "Rond",     radius: "1rem"      },
};

// ─── Default preferences ──────────────────────────────────────────────────────

const DEFAULTS: DesignPreferences = {
  accent: "brass",
  customAccentHex: "#B8943F",
  sidebar: "dark",
  appBackgroundHex: "#F7F3EC",
  surfaceBackgroundHex: "#EDE7DA",
  cardBackgroundHex: "#FFFFFF",
  headerBackgroundHex: "#FFFFFF",
  textHex: "#1C1914",
  mutedTextHex: "#6B6355",
  faintTextHex: "#A09585",
  sidebarBackgroundHex: "#1A1712",
  sidebarTextHex: "#C8BFB0",
  sidebarMutedHex: "#7A7060",
  sidebarHighlightHex: "#B8943F",
  font: "dm-sans",
  density: "normal",
  rounding: "soft",
};

// ─── Apply to DOM ─────────────────────────────────────────────────────────────

export function applyDesign(prefs: DesignPreferences) {
  const root = document.documentElement;

  // Accent color
  const accentData = prefs.accent === "custom"
    ? deriveAccentFromHex(prefs.customAccentHex)
    : ACCENT_PRESETS[prefs.accent];

  root.style.setProperty("--bb-brass",       accentData.base);
  root.style.setProperty("--bb-brass-light", accentData.light);
  root.style.setProperty("--bb-brass-dim",   accentData.dim);

  // Global surfaces and text colors
  root.style.setProperty("--bb-app-bg", prefs.appBackgroundHex);
  root.style.setProperty("--bb-surface-bg", prefs.surfaceBackgroundHex);
  root.style.setProperty("--bb-card-bg", prefs.cardBackgroundHex);
  root.style.setProperty("--bb-card-border", mixHex(prefs.surfaceBackgroundHex, prefs.textHex, 0.08));
  root.style.setProperty("--bb-header-bg", prefs.headerBackgroundHex);
  root.style.setProperty("--bb-ink", prefs.textHex);
  root.style.setProperty("--bb-ink-muted", prefs.mutedTextHex);
  root.style.setProperty("--bb-ink-faint", prefs.faintTextHex);
  root.style.setProperty("--bb-cream", prefs.appBackgroundHex);
  root.style.setProperty("--bb-parchment", prefs.surfaceBackgroundHex);
  root.style.setProperty("--bb-linen", mixHex(prefs.surfaceBackgroundHex, prefs.textHex, 0.06));

  // Sidebar colors
  root.style.setProperty("--bb-sidebar-bg", prefs.sidebarBackgroundHex);
  root.style.setProperty("--bb-sidebar-text", prefs.sidebarTextHex);
  root.style.setProperty("--bb-sidebar-muted", prefs.sidebarMutedHex);
  root.style.setProperty("--bb-sidebar-border", rgbaFromHex(prefs.sidebarTextHex, 0.12));
  root.style.setProperty("--bb-sidebar-hover", rgbaFromHex(prefs.sidebarHighlightHex, 0.12));
  root.style.setProperty("--bb-sidebar-active", rgbaFromHex(prefs.sidebarHighlightHex, 0.2));
  root.style.setProperty("--bb-sidebar-active-text", getReadableTextColor(prefs.sidebarHighlightHex, "#FFFFFF", prefs.sidebarTextHex));
  root.style.setProperty("--bb-sidebar-logo-filter", shouldUseInvertedLogo(prefs.sidebarBackgroundHex) ? "brightness(0) invert(1) opacity(0.9)" : "none");

  // Shadcn/tailwind semantic tokens
  root.style.setProperty("--background", prefs.appBackgroundHex);
  root.style.setProperty("--foreground", prefs.textHex);
  root.style.setProperty("--card", prefs.cardBackgroundHex);
  root.style.setProperty("--card-foreground", prefs.textHex);
  root.style.setProperty("--popover", prefs.cardBackgroundHex);
  root.style.setProperty("--popover-foreground", prefs.textHex);
  root.style.setProperty("--primary", accentData.base);
  root.style.setProperty("--primary-foreground", getReadableTextColor(accentData.base));
  root.style.setProperty("--secondary", prefs.surfaceBackgroundHex);
  root.style.setProperty("--secondary-foreground", prefs.textHex);
  root.style.setProperty("--muted", prefs.surfaceBackgroundHex);
  root.style.setProperty("--muted-foreground", prefs.mutedTextHex);
  root.style.setProperty("--accent", prefs.surfaceBackgroundHex);
  root.style.setProperty("--accent-foreground", prefs.textHex);
  root.style.setProperty("--border", mixHex(prefs.surfaceBackgroundHex, prefs.textHex, 0.12));
  root.style.setProperty("--input", mixHex(prefs.surfaceBackgroundHex, prefs.textHex, 0.12));
  root.style.setProperty("--ring", accentData.base);

  // Font
  const fontData = FONT_CHOICES[prefs.font];
  root.style.setProperty("--font-body", fontData.family);
  document.body.style.fontFamily = fontData.family;

  // Density
  const densityData = DENSITY_OPTIONS[prefs.density];
  root.style.setProperty("--bb-content-spacing", densityData.spacing);
  root.style.setProperty("--bb-card-padding",    densityData.cardPadding);

  // Border radius
  const roundingData = ROUNDING_OPTIONS[prefs.rounding];
  root.style.setProperty("--radius", roundingData.radius);
}

/** Derives a light variant and dim overlay from a hex color. */
function deriveAccentFromHex(hex: string): { base: string; light: string; dim: string } {
  // Parse hex to rgb
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Light = 20% brighter (mix with white)
  const lr = Math.min(255, Math.round(r + (255 - r) * 0.25));
  const lg = Math.min(255, Math.round(g + (255 - g) * 0.25));
  const lb = Math.min(255, Math.round(b + (255 - b) * 0.25));
  const light = `#${lr.toString(16).padStart(2, "0")}${lg.toString(16).padStart(2, "0")}${lb.toString(16).padStart(2, "0")}`;
  const dim = `rgba(${r},${g},${b},0.12)`;
  return { base: hex, light, dim };
}

function rgbaFromHex(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function mixHex(baseHex: string, mixWithHex: string, weight: number): string {
  const base = hexToRgb(baseHex);
  const mix = hexToRgb(mixWithHex);
  const r = Math.round(base.r + (mix.r - base.r) * weight);
  const g = Math.round(base.g + (mix.g - base.g) * weight);
  const b = Math.round(base.b + (mix.b - base.b) * weight);
  return rgbToHex(r, g, b);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function getReadableTextColor(
  backgroundHex: string,
  light = "#FFFFFF",
  dark = "#1C1914"
): string {
  const { r, g, b } = hexToRgb(backgroundHex);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.6 ? dark : light;
}

function shouldUseInvertedLogo(backgroundHex: string): boolean {
  return getReadableTextColor(backgroundHex) === "#FFFFFF";
}

// ─── Zustand store ────────────────────────────────────────────────────────────

interface DesignState {
  prefs: DesignPreferences;
  setPrefs: (partial: Partial<DesignPreferences>) => void;
  resetPrefs: () => void;
}

export const useDesignStore = create<DesignState>()(
  persist(
    (set, get) => ({
      prefs: DEFAULTS,

      setPrefs: (partial) => {
        const next = { ...get().prefs, ...partial };
        set({ prefs: next });
        applyDesign(next);
      },

      resetPrefs: () => {
        set({ prefs: DEFAULTS });
        applyDesign(DEFAULTS);
      },
    }),
    {
      name: "bb-design-prefs",
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<DesignState> | undefined;
        return {
          ...currentState,
          ...persisted,
          prefs: {
            ...DEFAULTS,
            ...(persisted?.prefs ?? {}),
          },
        };
      },
    }
  )
);
